<template>
  <div v-if="activeTab" class="max-w-lg mx-auto lg:max-w-none">
    <LandingHeader
      v-if="landingMode"
      :corridor-amount="corridor.amount || corridor.defaultAmount"
      :providers-count="results.providerQuotes.length"
      :timestamp="results.start"
    />
    <AffiliateDisclosure class="lg:hidden" v-if="!IS_MOBILE_APP" />
    <HeaderMessage
      v-if="filterError"
      class="lg:text-18 mt-4 lg:mt-8 mb-5 lg:mb-7"
      type="warning"
      dismissible
    >
      {{ filterError }}
    </HeaderMessage>
    <HeaderMessage
      v-if="filterMessage"
      class="lg:text-18 mt-4 lg:mt-8 mb-5 lg:mb-7"
      type="success"
      dismissible
    >
      {{ filterMessage }}
    </HeaderMessage>
    <Tabs
      v-if="showTabs"
      :tabs="tabs"
      :currency-from="corridor.currencyFrom.iso"
      :currency-to="corridor.currencyTo.iso"
      :corridor-amount="corridor.amount || corridor.defaultAmount"
      :active-tab="activeTabType"
    />
    <PoorResults
      v-if="!isGoodResults && !landingMode"
      :results-count="activeTab.items.length"
      :comparison-id="results.comparisonId"
      :current-tab="activeTabType"
    />
    <ul v-if="spotlight && $features?.getValue('hide_spotlight') !== 'on'" class="my-8">
      <ProviderCard
        :key="spotlight.psp.slug"
        :mid-market-rate="results.midMarket.rate"
        :grouped-quotes="spotlight"
        with-label
      />
    </ul>
    <ComparisonHeader
      :providers-count="activeTab.items.length"
      :current-tab="activeTabType"
      :tabs-length="tabs.length"
      :has-spotlight="spotlight !== undefined"
      :landing-mode="landingMode"
      :results="results"
    />
    <div v-if="showSpinner" class="fixed inset-0 bg-black/50 z-40 flex">
      <MSpinner class="self-center mx-auto" stroke-width="3" />
    </div>
    <ResultsList
      v-for="{ type, items } in tabs"
      :key="type"
      :quotes="items"
      :tab="type"
      :active-tab="activeTabType"
      :comparison-id="results.comparisonId"
      :mid-market-rate="results.midMarket.rate"
    />
    <p class="text-12 lg:text-14">
      {{ $t('transfer_comparison.disclaimer') }}
    </p>
    <p class="mt-4">
      <a class="text-14 link dark font-normal" :href="reverseCorridorUrl">
        {{
          $t('transfer_comparison.reverse_corridor_label', {
            toCountry: corridor.countryFrom.labels.to,
            fromCountry: corridor.countryTo.labels.from,
          })
        }}
      </a>
    </p>
    <ReportProblem class="mt-2 lg:hidden" :current-tab="activeTabType" />
    <CrossSellTiles class="lg:w-2/3" />
    <AlertPopup
      v-if="!IS_MOBILE_APP"
      :comparison-id="results.comparisonId"
      :current-tab="activeTabType"
      :cheapest-provider="cheapest.psp"
    />
    <AppPromptHiddenPromos
      v-if="!IS_MOBILE_APP && isMobileDevice && !landingMode"
      :prompt-type="promptType ?? 'generic'"
      :incentive
      :promo-providers="promoProviders"
    />
  </div>
</template>

<script lang="ts" setup>
import type { PayIn } from '@core/types/generated'
import type { Corridor, PaymentType } from '@core/types/imt'
import type { GQLTransferComparison } from './generated/transfer-comparison'
import type { Tab, HistoricalData } from './types'
import { computeTabs } from './helpers/process-tabs'
import { isRecommendedProvider } from './helpers/recommended'
import ResultsList from './results-list.vue'
import LandingHeader from './landing-header.vue'
import ComparisonHeader from './comparison-header.vue'
import Tabs from './tabs/tabs.vue'
import ProviderCard from './provider-card.vue'
import ReportProblem from './report-problem.vue'
import AffiliateDisclosure from './affiliate-disclosure.vue'
import PoorResults from './poor-results.vue'
import AlertPopup from './alert-popup.vue'
import HeaderMessage from './header-message.vue'
import { Comparison } from './analytics/objects/Comparison'
import { ComparisonSection } from './analytics/objects/ComparisonSection'
import { ComparisonResult } from './analytics/objects/ComparisonResult'
import { comparisonView } from './analytics/actions'
import type { PromptPromosType } from '@core/components/app-prompt/types'
import CrossSellTiles from './cross-sell-tiles/cross-sell-tiles.vue'

const IS_MOBILE_APP = __IS_MOBILE_APP__

const props = defineProps<{
  landingMode: boolean
  results: GQLTransferComparison['results']
  loadingTime: { start: number; end: number }
  historicalData?: HistoricalData
}>()

const { $lang, $features, $track, $i18next, $t, $cookie, $subscribePushNotification } = useNuxtApp()
const corridor = inject('corridor') as Corridor
const route = useRoute()
const liveUSDRate = useLiveExchangeRate(corridor.currencyTo.iso, 'USD')
const isMobileDevice = computed(() => ($cookie.value.getDevice() || 'desktop') !== 'desktop')

const showSpinner = ref(false)
function runSpinner() {
  showSpinner.value = true
  setTimeout(() => {
    showSpinner.value = false
  }, 600)
}

const usedProvidersFilter = ref<string[]>([])

onMounted(async () => {
  if (!props.landingMode) {
    usedProvidersFilter.value = await loadUsedProviders()
  }
  await saveComparisonInfo()
  await comparisonView($track, comparisonAnalyticsObj.value, sectionAnalyticsObj.value)
})

const comparisonAnalyticsObj = computed(() => {
  return Comparison.getInstance({
    loadingTime: props.loadingTime,
    corridor,
    data: props.results,
    tabs: tabs.value,
    filters: {
      lang: $lang,
      device: $cookie.value.getDevice() || 'desktop',
      payin: payinFilter.value,
      usedProviders: usedProvidersFilter.value,
    },
    comparisonPromosHidden: hidePromos.value,
  })
})
provide('comparisonAnalyticsObj', comparisonAnalyticsObj)

const sectionAnalyticsObj = computed(() => {
  if (!activeTab.value) return undefined
  const ranking = tabs.value.findIndex((t) => t.type === activeTabType.value) + 1
  const amount = corridor.amount || corridor.defaultAmount
  return ComparisonSection.getInstance({
    tab: activeTab.value,
    ranking,
    language: $lang,
    comparisonId: props.results.comparisonId,
    amount,
    amountUSD: props.results.corridor.amountUSD,
    midMarketRate: props.results.midMarket.rate,
    providersInUserList: activeTab.value.items
      .filter((i) => usedProvidersFilter.value.includes(i.psp.slug))
      .map((i) => i.psp.slug),
  })
})
provide('sectionAnalyticsObj', sectionAnalyticsObj)

function getResultAnalyticsObj(slug: string, ctaLocation?: string) {
  if (!activeTab.value) return undefined
  const result = activeTab.value.items.find((i) => i.psp.slug === slug)
  if (!result) return undefined
  return ComparisonResult.getInstance({
    result,
    ctaLocation,
    tab: activeTab.value,
    language: $lang,
    comparisonId: props.results.comparisonId,
    amount: corridor.amount || corridor.defaultAmount,
    amountUSD: props.results.corridor.amountUSD,
    midMarketRate: props.results.midMarket.rate,
  })
}
provide('getResultAnalyticsObj', getResultAnalyticsObj)

async function saveComparisonInfo() {
  const comparisonInfo = {
    countryFrom: corridor.countryFrom.iso,
    countryTo: corridor.countryTo.iso,
    currencyFrom: corridor.currencyFrom.iso,
    currencyTo: corridor.currencyTo.iso,
    amount: corridor.amount || corridor.defaultAmount,
    payout: activeTabType.value,
    cheapestProvider: {
      name: cheapest.value.psp.displayName,
      slug: cheapest.value.psp.slug,
    },
    spotlightedProvider: spotlight.value
      ? {
          name: spotlight.value.psp.displayName,
          slug: spotlight.value.psp.slug,
        }
      : null,
    liveComparison: !props.landingMode,
    midMarketRate: props.results.midMarket.rate,
    results: __IS_MOBILE_APP__ ? props.results : null,
    timestamp: Date.now(),
  }
  await storeLastComparisonInfo(comparisonInfo)

  if (__IS_MOBILE_APP__) {
    $subscribePushNotification(comparisonInfo)
  }
}

const payinFilter = computed(() => {
  if (route.query.payin) {
    if (Array.isArray(route.query.payin)) {
      return route.query.payin as PayIn[]
    }
    return [route.query.payin] as PayIn[]
  }
  return undefined
})

const hasFilteredPayins = computed(() => {
  if (!payinFilter.value) {
    return false
  }
  return props.results.providerQuotes.some(({ quotes }) =>
    quotes.some((q) => payinFilter.value?.includes(q.payin)),
  )
})

const filters = computed(() => {
  return {
    lang: $lang,
    device: __IS_MOBILE_APP__ ? 'mobile' : $cookie.value.getDevice() || 'desktop',
    payin: hasFilteredPayins.value ? payinFilter.value : undefined,
    usedProviders: usedProvidersFilter.value,
  }
})

const tabsWithPromos = computed((): Tab[] => {
  return computeTabs(props.results, corridor.countryTo.iso, filters.value, props.historicalData)
})

const tabsWithoutPromos = computed((): Tab[] => {
  return computeTabs(
    props.results,
    corridor.countryTo.iso,
    { ...filters.value, hidePromos: true },
    props.historicalData,
  )
})

const defaultTab = {
  withPromos: computed(() => tabsWithPromos.value[0].items),
  withoutPromos: computed(() => tabsWithoutPromos.value[0].items),
}

const promoProviders = computed(() => {
  return defaultTab.withPromos.value.map((quote) => {
    return {
      icon: quote.psp.logo.ico,
      slug: quote.psp.slug,
      displayName: quote.psp.displayName,
    }
  })
})

const promptType = computed<PromptPromosType | undefined>(() => {
  if (!isMobileDevice.value) {
    return undefined
  }
  const bestUSDPromoAmount =
    defaultTab.withPromos.value[0]?.cheapestService.amount * (liveUSDRate.value ?? 1)
  const bestUSDStandardAmount =
    defaultTab.withoutPromos.value[0]?.cheapestService.amount * (liveUSDRate.value ?? 1)
  const bestPromoProviderEI = defaultTab.withPromos.value[0]?.exposure ?? 0
  const bestStandardProviderEI = defaultTab.withoutPromos.value[0]?.exposure ?? 0
  const promoPriceDiffUSD = bestUSDPromoAmount - bestUSDStandardAmount
  if (promoPriceDiffUSD >= 3 && bestStandardProviderEI > bestPromoProviderEI) {
    return promoProviders.value.length >= 3 ? 'providers' : 'save'
  }
  return 'generic'
})

const incentive = computed(() => {
  if (promptType.value === 'save') {
    const promoAmount = defaultTab.withPromos.value[0]?.cheapestService.amount
    const standardAmount = defaultTab.withoutPromos.value[0]?.cheapestService.amount
    const promoIncentive = promoAmount - standardAmount
    return formatMoney(
      Math.max(1, Math.floor((1 / props.results.midMarket.rate) * promoIncentive)),
      corridor.currencyFrom.iso,
      $lang,
      0,
    )
  }
  return undefined
})

const hidePromos = computed(() => {
  return !__IS_MOBILE_APP__ && isMobileDevice.value && promptType.value !== 'generic'
})

const tabs = computed(() => {
  return hidePromos.value ? tabsWithoutPromos.value : tabsWithPromos.value
})

const routeTab = computed(() => {
  if (route.query.tab) {
    return tabs.value.find((t) => t.type === route.query.tab)
  }
  return undefined
})

const showTabs = computed(() => {
  if (__IS_MOBILE_APP__) {
    return !tabs.value.find((t) => t.type === corridor.payout) && tabs.value.length >= 1
  }
  return true
})

const activeTabType = computed(() => {
  return routeTab.value?.type ?? tabs.value[0]?.type
})
provide('activeTabType', activeTabType)

watch(
  () => activeTabType.value,
  async () => {
    runSpinner()
    await saveComparisonInfo()
    await comparisonView($track, comparisonAnalyticsObj.value, sectionAnalyticsObj.value)
  },
)

const activeTab = computed(() => {
  return tabs.value.find((p) => p.type === activeTabType.value)
})

const cheapest = computed(() => {
  return activeTab.value?.items.find((p) => p.bestDeal) ?? tabs.value[0].items[0]
})

const spotlight = computed(() => {
  const candidate = activeTab.value?.items.find((p) => p.label)
  if (__IS_MOBILE_APP__ && candidate?.label === 'sponsored') {
    return undefined
  }
  return candidate
})

const isGoodResults = computed(() => {
  return activeTab.value?.items.some(({ psp }) => isRecommendedProvider(psp))
})

const filterError = computed(() => {
  const tab = route.query.tab
  if (
    corridor.payout &&
    !tabs.value.find((t) => t.type === (corridor.payout as PaymentType))?.type &&
    $i18next.exists(`tabs.no_results_${corridor.payout}`)
  ) {
    return $t(`tabs.no_results_${corridor.payout}`)
  }

  if (
    tab &&
    typeof tab === 'string' &&
    !routeTab.value?.type &&
    $i18next.exists(`tabs.no_results_${tab}`)
  ) {
    return $t(`tabs.no_results_${tab}`)
  }

  if (payinFilter.value && !hasFilteredPayins.value) {
    return $t('imt_no_results.no_results_for_payin')
  }

  return null
})

const filterMessage = computed(() => {
  if (
    usedProvidersFilter.value.length > 0 &&
    props.results.providerQuotes.some(({ psp }) => usedProvidersFilter.value.includes(psp.slug)) &&
    !__IS_MOBILE_APP__
  ) {
    return $t('promotion.first_time_promos_removed')
  }
  return null
})

const reverseCorridorUrl = computed(() => {
  return getTransferUrl({
    countryFrom: corridor.countryTo,
    countryTo: corridor.countryFrom,
    currencyFrom: corridor.currencyTo,
    currencyTo: corridor.currencyFrom,
  })
})
</script>
